/**
 * Latency — measures round-trip time to the host.
 *
 * Clients send a PING with `performance.now()`; the host bounces a PONG with
 * the same timestamp; we compute RTT and store a rolling average. The result
 * is exposed for the latency widget and for snapshot interpolation.
 */
import { MSG } from './Sync.js';

export class Latency {
  /**
   * @param {object} opts
   * @param {import('./Network.js').Network} opts.network
   * @param {import('./Sync.js').Sync} opts.sync
   * @param {number} [opts.intervalMs=1000]
   * @param {number} [opts.windowSize=8]
   */
  constructor(opts) {
    this.net = opts.network;
    this.sync = opts.sync;
    this.intervalMs = opts.intervalMs || 1000;
    this.windowSize = opts.windowSize || 8;

    this._samples = [];
    this._timer = null;
    this._lastRttMs = 0;

    // Hook the sync layer's PONG handler.
    this.sync._onPong = (data) => this._handlePong(data);
  }

  start() {
    if (this._timer || this.net.isHost) return;
    this._timer = setInterval(() => this._sendPing(), this.intervalMs);
  }

  stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  getRttMs() {
    return this._lastRttMs;
  }

  getAverageRttMs() {
    if (this._samples.length === 0) return 0;
    let sum = 0;
    for (const s of this._samples) sum += s;
    return sum / this._samples.length;
  }

  _sendPing() {
    if (!this.net.hostConnection || !this.net.hostConnection.open) return;
    this.net.sendToHost({ t: MSG.PING, ts: performance.now() });
  }

  _handlePong(data) {
    const rtt = performance.now() - data.ts;
    this._lastRttMs = rtt;
    this._samples.push(rtt);
    if (this._samples.length > this.windowSize) this._samples.shift();
  }
}
